import { normalizeNamespace } from './client';

/**
 * Items API service
 */
export class ItemsApi {
  constructor(apiClient) {
    this.client = apiClient;
  }

  /**
   * Get paginated list of items
   */
  getItems(namespace, params = {}) {
    const ns = normalizeNamespace(namespace);
    const {
      offset, limit, listId, tagId, view, sortby, source, filter,
      tag = [], author = [], site = [], list = [], type = [],
    } = params;
    // Drill-in (single listId/tagId) and FilterBar facets are the same wire
    // concept — merge into one comma-separated value per facet.
    const tags = [...(tagId ? [tagId] : []), ...tag];
    const lists = [...(listId ? [listId] : []), ...list];
    const queryParams = {
      ...(offset !== undefined && { offset }),
      ...(limit !== undefined && { limit }),
      ...(lists.length && { list: lists.join(',') }),
      ...(tags.length && { tag: tags.join(',') }),
      ...(author.length && { author: author.join(',') }),
      ...(site.length && { site: site.join(',') }),
      ...(type.length && { type: type.join(',') }),
      ...(view && { view }),
      ...(sortby && { sortby }),
      ...(source && { source }),
      ...(filter && { filter }),
    };
    return this.client.get(`/${ns}/items`, queryParams);
  }

  /**
   * Get single item by ID
   */
  getItem(namespace, itemId) {
    const ns = normalizeNamespace(namespace);
    return this.client.get(`/${ns}/items/${itemId}`);
  }

  /**
   * Get item extra data (HN, Reddit, GitHub, YouTube, Lobsters)
   */
  getItemExtra(namespace, itemId) {
    const ns = normalizeNamespace(namespace);
    return this.client.get(`/${ns}/items/${itemId}/extras`);
  }

  /**
   * Fetch metadata for each of the item's URLs that was never fetched, AI's
   * included (moderator only). Answers `{ fetched, ai }` counts.
   */
  enhanceUnfetchedItemUrls(namespace, itemId) {
    const ns = normalizeNamespace(namespace);
    return this.client.post(`/${ns}/items/${itemId}/extras`);
  }

  /**
   * Get per-field item metadata candidates
   */
  suggestItemMetadata(namespace, itemId) {
    const ns = normalizeNamespace(namespace);
    return this.client.post(`/${ns}/items/${itemId}/suggest`);
  }

  /**
   * Create new item
   */
  createItem(namespace, item, listId) {
    const ns = normalizeNamespace(namespace);
    const endpoint = listId ? `/${ns}/lists/${listId}/items` : `/${ns}/items`;
    return this.client.post(endpoint, item);
  }

  /**
   * Bookmark a public item — creates the user's private copy of it.
   */
  bookmarkItem(itemId) {
    return this.client.post('/private/items', { id: itemId });
  }

  /**
   * Update item
   */
  updateItem(namespace, itemId, patch, { reviewed = false } = {}) {
    const ns = normalizeNamespace(namespace);
    const query = reviewed ? '?reviewed=true' : '';
    return this.client.patch(`/${ns}/items/${itemId}${query}`, patch);
  }

  /**
   * Delete item
   */
  deleteItem(namespace, itemId) {
    const ns = normalizeNamespace(namespace);
    return this.client.delete(`/${ns}/items/${itemId}`);
  }

  /**
   * Batch update items
   */
  batchUpdate(namespace, ids, patch) {
    const ns = normalizeNamespace(namespace);
    return this.client.patch(`/${ns}/items`, { ids, patch });
  }

  /**
   * Batch delete items
   */
  batchDelete(namespace, ids) {
    const ns = normalizeNamespace(namespace);
    return this.client.delete(`/${ns}/items`, { ids });
  }

  /**
   * Batch import items from URLs
   */
  batchImport(urls) {
    return this.client.post('/private/items/batch', { urls });
  }

  /**
   * Merge multiple items into one
   */
  mergeItems(namespace, targetItemId, sourceItemIds) {
    const ns = normalizeNamespace(namespace);
    return this.client.patch(`/${ns}/items/${targetItemId}/merge`, { itemIds: sourceItemIds });
  }

  /**
   * Clone an item
   */
  cloneItem(namespace, itemId) {
    const ns = normalizeNamespace(namespace);
    return this.client.patch(`/${ns}/items/${itemId}/clone`);
  }

  /**
   * Share item (create pending copy for approval)
   */
  shareItem(itemId) {
    return this.client.post(`/pending/items/${itemId}`);
  }

  /**
   * Get items by author
   */
  getItemsByAuthor(namespace, authorId, params = {}) {
    const ns = normalizeNamespace(namespace);
    const {
      offset, limit, sortby, source, filter,
      tag = [], author = [], site = [], type = [],
    } = params;
    const authors = [authorId, ...author];
    return this.client.get(`/${ns}/items`, {
      author: authors.join(','),
      ...(offset !== undefined && { offset }),
      ...(limit !== undefined && { limit }),
      ...(tag.length && { tag: tag.join(',') }),
      ...(site.length && { site: site.join(',') }),
      ...(type.length && { type: type.join(',') }),
      ...(sortby && { sortby }),
      ...(source && { source }),
      ...(filter && { filter }),
    });
  }

  /**
   * Get items by site
   */
  getItemsBySite(namespace, siteId, params = {}) {
    const ns = normalizeNamespace(namespace);
    const {
      offset, limit, sortby, source, filter,
      tag = [], author = [], site = [], type = [],
    } = params;
    const sites = [siteId, ...site];
    return this.client.get(`/${ns}/items`, {
      site: sites.join(','),
      ...(offset !== undefined && { offset }),
      ...(limit !== undefined && { limit }),
      ...(tag.length && { tag: tag.join(',') }),
      ...(author.length && { author: author.join(',') }),
      ...(type.length && { type: type.join(',') }),
      ...(sortby && { sortby }),
      ...(source && { source }),
      ...(filter && { filter }),
    });
  }
}

export default ItemsApi;
